/* eslint-disable react/require-default-props */
import React from "react";
import styled from "@emotion/styled";
import { Input, Select, Space } from "antd";
import { KeyboardArrowDownOutlined } from "@mui/icons-material";
import { COUNTRIES, Country } from "./constants/countries";

const COUNTRY_BY_ISO2 = new Map(COUNTRIES.map((country) => [country.iso2, country]));

const COUNTRY_OPTIONS = COUNTRIES.map((country) => ({
  value: country.iso2,
  label: `${country.flag} ${country.name} ${country.dialCode}`,
}));

// antd centres the arrow and clear icons on the outer .ant-select box, which stays at its
// default 32px while app.scss forces the visible .ant-select-selector to 40px, so they sit high.
// antd also offsets them for a 12px glyph and hides the arrow on hover only by painting the
// 12px clear icon over it, which the 20px arrow used across this app pokes out from under.
const PhoneCompact = styled(Space.Compact)`
  .ant-select {
    height: 40px;
  }
  .ant-select-arrow,
  .ant-select-clear {
    top: 50%;
    margin-top: 0;
    height: auto;
    display: flex;
    align-items: center;
    transform: translateY(-50%);
  }
  .country-selected:hover .ant-select-arrow {
    opacity: 0;
  }
`;

// Separators people type out of habit. Anything else stays in, so the validator can reject it
// instead of the number being silently changed.
const SEPARATORS = /[\s\-.()]/g;

// E.164 caps a number at 15 digits, country code included. Below 4 national digits nothing
// real exists.
const MAX_E164_DIGITS = 15;
const MIN_NATIONAL_DIGITS = 4;

const filterCountry = (input: string, option?: { value: string }) => {
  const country = option && COUNTRY_BY_ISO2.get(option.value);
  if (!country) return false;
  const query = input.trim().toLowerCase();
  return (
    country.name.toLowerCase().includes(query) ||
    country.iso2.toLowerCase() === query ||
    country.dialCode.startsWith(query.startsWith("+") ? query : `+${query}`)
  );
};

/**
 * Structural check only: a known dial code followed by a plausible count of digits. Whether the
 * number really belongs to that country is decided by the backend.
 */
export function isPlausiblePhoneNumber(value: string): boolean {
  // Country calling codes are prefix-free, so at most one can match.
  const country = COUNTRIES.find(({ dialCode }) => value.startsWith(dialCode));
  if (!country) return false;
  const national = value.slice(country.dialCode.length);
  return (
    /^\d+$/.test(national) &&
    national.length >= MIN_NATIONAL_DIGITS &&
    value.length - 1 <= MAX_E164_DIGITS
  );
}

interface IPhoneNumberInputProps {
  id?: string;
  // Injected by Form.Item. Emits the concatenated number ("+32470123456"), or "" until both a
  // country and a number are given, so the form's `required` rule still applies. `value` is only
  // read to skip no-op changes: nothing prefills a phone field.
  value?: string;
  onChange?: (value: string) => void;
}

function PhoneNumberInput({ id, value, onChange }: IPhoneNumberInputProps) {
  // The country is kept here rather than parsed back out of the value: +1 alone is shared by
  // the US, Canada and much of the Caribbean.
  const [iso2, setIso2] = React.useState<string | undefined>();
  const [number, setNumber] = React.useState("");

  const emit = (country: Country | undefined, raw: string) => {
    const digits = raw.replace(SEPARATORS, "");
    const next = country && digits ? `${country.dialCode}${digits}` : "";
    // The form validates on every change, so picking a country before typing would otherwise
    // flag the field as required straight away.
    if (next !== (value ?? "")) onChange?.(next);
  };

  const handleCountryChange = (next?: string) => {
    setIso2(next);
    emit(next ? COUNTRY_BY_ISO2.get(next) : undefined, number);
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNumber(event.target.value);
    emit(iso2 ? COUNTRY_BY_ISO2.get(iso2) : undefined, event.target.value);
  };

  return (
    <PhoneCompact block>
      <Select
        aria-label='Country'
        className={iso2 ? "country-selected" : undefined}
        allowClear
        showSearch
        placeholder='Country'
        value={iso2}
        onChange={handleCountryChange}
        options={COUNTRY_OPTIONS}
        filterOption={filterCountry}
        labelRender={(selected) => {
          const country = COUNTRY_BY_ISO2.get(String(selected.value));
          return country ? `${country.flag} ${country.dialCode}` : null;
        }}
        popupMatchSelectWidth={false}
        style={{ width: 130, flexShrink: 0 }}
        suffixIcon={
          <KeyboardArrowDownOutlined sx={{ height: "20px", width: "20px", color: "#AEB6BE" }} />
        }
      />
      <Input
        id={id}
        type='tel'
        inputMode='tel'
        autoComplete='tel-national'
        disabled={!iso2}
        placeholder='Phone number'
        value={number}
        onChange={handleNumberChange}
      />
    </PhoneCompact>
  );
}

export default PhoneNumberInput;
